import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Виртуальный скролл для больших таблиц (инвентаризация, документы).
 *
 * В DOM держим только видимые строки + запас сверху/снизу, а место остальных
 * занимают две пустые строки-распорки. Скроллбар при этом остаётся правильной
 * длины, а количество узлов не растёт вместе с документом.
 *
 * Контейнер (div со скроллом, например .table-fixed) вешается через containerRef.
 *
 * Использование:
 *   const virtual = useVirtualRows({ count: data.elements.length })
 *   <div className="table-responsive table-fixed" ref={virtual.containerRef}>
 *     <table>
 *       <tbody>
 *         {virtual.topSpacer > 0 && <tr style={{ height: virtual.topSpacer }} />}
 *         {data.elements.slice(virtual.start, virtual.end).map((item, i) => {
 *           const index = virtual.start + i
 *           ...
 *         })}
 *         {virtual.bottomSpacer > 0 && <tr style={{ height: virtual.bottomSpacer }} />}
 *       </tbody>
 *     </table>
 *   </div>
 *
 * @param count      сколько всего строк
 * @param rowHeight  высота обычной строки в px (должна совпадать с CSS)
 * @param rowHeights массив высот, если строки разной высоты (строки с группами)
 * @param overscan   сколько строк рендерить сверх экрана с каждой стороны
 * @param threshold  до какого количества строк виртуализация не включается
 */
function useVirtualRows({
	count = 0,
	rowHeight = 38,
	rowHeights = null,
	overscan = 8,
	threshold = 60,
}) {
	const containerRef = useRef(null)
	const offsetsRef = useRef({ offsets: [], total: 0 })

	const [range, setRange] = useState({ start: 0, end: count })

	const enabled = count > threshold

	// Позиции строк пересчитываем на каждом рендере: это O(n) сложений,
	// для 2000 строк — доли миллисекунды, зато высоты всегда актуальные.
	var offsets = new Array(count)
	var total = 0
	for (let i = 0; i < count; i++) {
		offsets[i] = total
		total += (rowHeights && rowHeights[i]) ? rowHeights[i] : rowHeight
	}
	offsetsRef.current = { offsets: offsets, total: total }

	// Первая строка, которая ещё видна при заданном scrollTop
	const findIndex = useCallback((scrollTop) => {
		const list = offsetsRef.current.offsets
		var low = 0
		var high = list.length - 1
		var result = 0

		while (low <= high) {
			const middle = (low + high) >> 1
			if (list[middle] <= scrollTop) {
				result = middle
				low = middle + 1
			} else {
				high = middle - 1
			}
		}

		return result
	}, [])

	const measure = useCallback(() => {
		const container = containerRef.current
		const list = offsetsRef.current.offsets

		if (!container || list.length === 0) {
			setRange((prev) => (prev.start === 0 && prev.end === list.length) ? prev : { start: 0, end: list.length })
			return
		}

		const scrollTop = container.scrollTop
		const viewport = container.clientHeight || 600

		var start = findIndex(scrollTop)
		var end = findIndex(scrollTop + viewport) + 1

		start = Math.max(0, start - overscan)
		end = Math.min(list.length, end + overscan)

		setRange((prev) => (prev.start === start && prev.end === end) ? prev : { start: start, end: end })
	}, [findIndex, overscan])

	// Считаем прямо в обработчике: бинарный поиск + сравнение диапазона стоят
	// доли миллисекунды, а ререндер происходит только когда окно реально сдвинулось
	useEffect(() => {
		if (!enabled) return

		const container = containerRef.current
		if (!container) return

		container.addEventListener('scroll', measure, { passive: true })
		window.addEventListener('resize', measure)

		return () => {
			container.removeEventListener('scroll', measure)
			window.removeEventListener('resize', measure)
		}
	}, [enabled, measure])

	useLayoutEffect(() => {
		if (!enabled) return
		measure()
	}, [enabled, count, total, measure])

	// Проскроллить к строке и (по желанию) сфокусировать её поле
	const scrollToIndex = useCallback((index, focusSelector) => {
		const container = containerRef.current
		const { offsets: list } = offsetsRef.current
		if (!container || index < 0) return

		if (list[index] !== undefined) {
			container.scrollTop = Math.max(0, list[index] - (container.clientHeight / 2))
			measure()
		}

		if (focusSelector) {
			setTimeout(() => {
				const field = document.getElementById(focusSelector)
				if (field) {
					field.focus()
					if (typeof field.select === 'function') field.select()
				}
			}, 60)
		}
	}, [measure])

	if (!enabled) {
		return {
			containerRef: containerRef,
			start: 0,
			end: count,
			topSpacer: 0,
			bottomSpacer: 0,
			virtualized: false,
			scrollToIndex: scrollToIndex,
		}
	}

	const start = Math.min(range.start, Math.max(0, count - 1))
	const end = Math.min(Math.max(range.end, start + 1), count)
	const lastVisible = end > 0 ? end - 1 : 0
	const bottomFrom = offsets[lastVisible] !== undefined
		? offsets[lastVisible] + ((rowHeights && rowHeights[lastVisible]) ? rowHeights[lastVisible] : rowHeight)
		: 0

	return {
		containerRef: containerRef,
		start: start,
		end: end,
		topSpacer: offsets[start] || 0,
		bottomSpacer: Math.max(0, total - bottomFrom),
		virtualized: true,
		scrollToIndex: scrollToIndex,
	}
}

export default useVirtualRows
